/**
 * Dati e azioni dei comandi del Lead/Cliente nel pannello chat: fase della
 * pipeline (o cambio pipeline se il lead non ne ha una), stato (client_states)
 * e assegnatario. Scrive col percorso canonico della scheda e del kanban
 * (clientStore.updateClient: workflow, cronologia stati, notifiche) e poi
 * ricarica la scheda per aggiornare subito il pannello.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useClientStore } from '@/lib/stores/clientStore';
import { useLeadPipelineStore } from '@/lib/stores/leadPipelineStore';
import { useCustomStatesStore } from '@/lib/stores/customStatesStore';
import { useUserStore } from '@/lib/stores/userStore';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { useApp } from '@/components/AppProvider';
import type { Client } from '@/lib/types';
import {
  toLeadStages, stageOptions, clientStateOptions, currentStageId, stageUpdate,
  pipelineUpdate, pipelineOptions, chatLeadErrorMessage,
} from './chatLeadControlsLogic';

type Field = 'stage' | 'pipeline' | 'state' | 'assignee' | 'fields';

export function useChatLeadControls(client: Client) {
  const { toast } = useApp();
  const { hasPermission } = usePermissions();
  const updateClient = useClientStore((s) => s.updateClient);
  const fetchClientById = useClientStore((s) => s.fetchClientById);
  const pipelines = useLeadPipelineStore((s) => s.pipelines);
  const stagesMap = useLeadPipelineStore((s) => s.stages);
  const fetchPipelines = useLeadPipelineStore((s) => s.fetchPipelines);
  const fetchStagesByPipeline = useLeadPipelineStore((s) => s.fetchStagesByPipeline);
  const { leadStates, clientStates, fetchLeadStates, fetchClientStates } = useCustomStatesStore();
  const { users, fetchUsers } = useUserStore();
  const [saving, setSaving] = useState<Field | null>(null);

  // Lead o Cliente dallo stato (client_states): fetchClientById non porta la join
  // client_state, quindi si legge dall'elenco degli stati. Senza stato vale come Cliente.
  const stateRow = clientStates.find((s) => s.id === client.stato_cliente);
  const isLead = !!(stateRow ? stateRow.is_lead : client.client_state?.is_lead);
  const canEdit = hasPermission(isLead ? 'leads' : 'clients', 'update');
  const pipelineId = client.pipeline_id || null;

  // Dati di appoggio: solo quello che manca (gli store hanno gia' la loro cache).
  useEffect(() => {
    if (pipelines.length === 0) void fetchPipelines();
    if (leadStates.length === 0) void fetchLeadStates();
    if (clientStates.length === 0) void fetchClientStates();
    if (users.length === 0) void fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (pipelineId) void fetchStagesByPipeline(pipelineId); }, [pipelineId, fetchStagesByPipeline]);

  const stages = useMemo(
    () => toLeadStages(pipelineId ? stagesMap.get(pipelineId) || [] : leadStates),
    [pipelineId, stagesMap, leadStates],
  );
  const pipeline = pipelineId ? pipelines.find((p) => p.id === pipelineId) || null : null;
  const userOptions = useMemo(
    () => users.map((u) => ({ id: u.id, email: u.email, full_name: u.full_name, profile_image_url: u.profile_image_url })),
    [users],
  );

  const save = useCallback(async (field: Field, updates: Partial<Client>, okText: string, errText: string) => {
    setSaving(field);
    try {
      const result = await updateClient(client.id, updates);
      if (!result) throw new Error('Update failed');
      toast.success(okText);
      // Rilegge la scheda dal database (assegnatario e dati collegati aggiornati).
      void fetchClientById(client.id, true);
      return true;
    } catch (e) {
      console.error('[useChatLeadControls] aggiornamento non riuscito:', e);
      toast.error(chatLeadErrorMessage(e, errText));
      return false;
    } finally {
      setSaving(null);
    }
  }, [client.id, updateClient, fetchClientById, toast]);

  const setStage = useCallback((stageId: string) =>
    save('stage', stageUpdate(!!pipelineId, stageId) as Partial<Client>, 'Fase aggiornata', 'Non sono riuscito a cambiare la fase'),
  [save, pipelineId]);

  const setPipeline = useCallback(async (newPipelineId: string) => {
    let pipelineStages = useLeadPipelineStore.getState().getStagesForPipeline(newPipelineId);
    if (pipelineStages.length === 0) {
      await fetchStagesByPipeline(newPipelineId);
      pipelineStages = useLeadPipelineStore.getState().getStagesForPipeline(newPipelineId);
    }
    const updates = pipelineUpdate(newPipelineId, pipelineStages);
    if (!updates) {
      toast.error('Questa pipeline non ha fasi: aggiungile dalle impostazioni della pipeline');
      return false;
    }
    return save('pipeline', updates as unknown as Partial<Client>, 'Spostato nella pipeline', 'Non sono riuscito a spostarlo nella pipeline');
  }, [save, fetchStagesByPipeline, toast]);

  const setState = useCallback((stateId: string) =>
    save('state', { stato_cliente: stateId, last_activity_date: new Date().toISOString() } as Partial<Client>, 'Stato aggiornato', 'Non sono riuscito a cambiare lo stato'),
  [save]);

  const setAssignee = useCallback((userId: string) =>
    save('assignee', { assigned_to: userId || null, last_activity_date: new Date().toISOString() } as Partial<Client>, userId ? 'Assegnatario aggiornato' : 'Assegnazione tolta', "Non sono riuscito a cambiare l'assegnatario"),
  [save]);

  // Dati della scheda (email, ragione sociale...) confermati da un suggerimento dell'assistente.
  const updateFields = useCallback((updates: Record<string, string>) =>
    save('fields', { ...updates, last_activity_date: new Date().toISOString() } as Partial<Client>, 'Dati aggiornati', 'Non sono riuscito ad aggiornare i dati'),
  [save]);

  return {
    canEdit,
    isLead,
    saving,
    pipelineId,
    pipelineName: pipelineId ? pipeline?.name || 'Pipeline' : null,
    stages,
    stageOptions: useMemo(() => stageOptions(stages), [stages]),
    currentStageId: currentStageId(client),
    pipelineOptions: useMemo(() => pipelineOptions(pipelines), [pipelines]),
    stateOptions: useMemo(() => clientStateOptions(clientStates), [clientStates]),
    userOptions,
    setStage, setPipeline, setState, setAssignee, updateFields,
  };
}
